import { queryOptions } from '@tanstack/react-query';

import type { MfaVerificationProof } from '@/api/mfa/types';
import { queryClient } from '@/app/providers/react-query-provider';

import { TFetchError } from '../api-error.types';
import { apiInvestingViewsFetch, apiInvestmentFetch } from '../index';
import { SubscriptionDto, WizardResumeResponse } from './subscription-projection';
import { InvestmentPaymentMethod, SubscriptionDocumentRef } from './types';

export const SUBSCRIPTION_QUERY_KEYS = {
  wizardResume: 'investing-wizard-resume',
  activeSubscription: 'investing-active-subscription',
};

export interface WizardResumeParams {
  fundId?: string;
  /** Resume a specific attempt; omitted, the fund's active subscription is resolved. */
  subscriptionId?: string | null;
}

export interface AgreementResponse {
  documentId: string;
  signingToken: string;
  /** Null once the document is no longer pending signature. */
  signingUrl: string | null;
  title: string;
}

export interface WireAttestationBody {
  /** ISO 8601. */
  dateSent: string;
  sendingBank: string;
  /** Decimal string, matching the amount the subscription was started with. */
  amount: string;
  reference: string;
}

// ==================== Reads ====================

export const _getWizardResume = (params: {
  fundId: string;
  subscriptionId?: string | null;
}): Promise<WizardResumeResponse> =>
  apiInvestingViewsFetch<WizardResumeResponse>('/wizard-resume', {
    query: params.subscriptionId
      ? { fundId: params.fundId, subscriptionId: params.subscriptionId }
      : { fundId: params.fundId },
  });

/**
 * The wizard's whole load-time read. Refetches on mount because the investor
 * can advance the same subscription from another tab — or come back after
 * signing in Documenso, which lands on the wizard with the agreement already
 * signed server-side.
 */
export const _getWizardResumeQuery = (params: WizardResumeParams, enabled = true) =>
  queryOptions({
    enabled: enabled && !!params.fundId,
    queryKey: [SUBSCRIPTION_QUERY_KEYS.wizardResume, params.fundId, params.subscriptionId ?? null],
    queryFn: () =>
      _getWizardResume({ fundId: params.fundId as string, subscriptionId: params.subscriptionId }),
    refetchOnMount: true,
  });

/**
 * Every wizard step changes the subscription the resume read returns, so the
 * mutation layer resyncs through this rather than patching the cache by hand.
 * Keyed on the fund alone, so both the "active subscription" and the
 * "resume this attempt" variants refresh together.
 */
export const invalidateWizardResume = (fundId: string) =>
  queryClient.invalidateQueries({ queryKey: [SUBSCRIPTION_QUERY_KEYS.wizardResume, fundId] });

export interface ActiveSubscriptionResponse {
  /** Null when the investor has never started a subscription in this fund, or cancelled the last attempt. */
  subscription: SubscriptionDto | null;
}

/**
 * The fund page's own read of the caller's in-flight subscription in a fund
 * (GP-3985) — narrower than `_getWizardResume`, which additionally carries the
 * agreement signing session. Answers with a null subscription rather than a
 * 404 for the same reason `_getWizardResume` does.
 */
export const _getActiveSubscription = (fundId: string): Promise<ActiveSubscriptionResponse> =>
  apiInvestmentFetch<ActiveSubscriptionResponse>('/subscriptions/active', { query: { fundId } });

export const _getActiveSubscriptionQuery = (fundId?: string) =>
  queryOptions({
    enabled: !!fundId,
    queryKey: [SUBSCRIPTION_QUERY_KEYS.activeSubscription, fundId],
    queryFn: () => _getActiveSubscription(fundId as string),
    refetchOnMount: true,
  });

export const invalidateActiveSubscription = (fundId: string) =>
  queryClient.invalidateQueries({
    queryKey: [SUBSCRIPTION_QUERY_KEYS.activeSubscription, fundId],
  });

// ==================== Wizard steps ====================

export const _postStartSubscription = (body: {
  fundId: string;
  amount: string;
  denomination: string;
}): Promise<{ id: string }> =>
  apiInvestmentFetch<{ id: string }>('/subscriptions', { method: 'POST', body });

export const _postSubmitEligibility = (
  subscriptionId: string,
  verification?: MfaVerificationProof,
): Promise<void> =>
  apiInvestmentFetch<void>(`/subscriptions/${subscriptionId}/eligibility`, {
    method: 'POST',
    body: verification ? { verification } : {},
  });

/**
 * `renew` asks for a fresh document; without it a subscription that already has
 * one gets that same document's signing session back.
 */
export const _postRequestAgreement = (
  subscriptionId: string,
  body: { renew?: boolean } = {},
): Promise<AgreementResponse> =>
  apiInvestmentFetch<AgreementResponse>(`/subscriptions/${subscriptionId}/agreement`, {
    method: 'POST',
    body,
  });

/** The payload is the complete set — documents left out of it are detached. */
export const _putSupportingDocuments = (
  subscriptionId: string,
  documents: SubscriptionDocumentRef[],
): Promise<void> =>
  apiInvestmentFetch<void>(`/subscriptions/${subscriptionId}/supporting-documents`, {
    method: 'PUT',
    body: { documents },
  });

export const _postAcknowledgeData = (subscriptionId: string): Promise<void> =>
  apiInvestmentFetch<void>(`/subscriptions/${subscriptionId}/acknowledgment`, { method: 'POST' });

/** A wire selection answers with the reference to quote; a crypto selection with null. */
export const _postSelectPaymentMethod = (
  subscriptionId: string,
  method: InvestmentPaymentMethod,
): Promise<{ paymentReference: string | null }> =>
  apiInvestmentFetch<{ paymentReference: string | null }>(
    `/subscriptions/${subscriptionId}/payment-method`,
    { method: 'POST', body: { method } },
  );

export const _postConfirmFundsSent = (
  subscriptionId: string,
  attestation: WireAttestationBody,
): Promise<void> =>
  apiInvestmentFetch<void>(`/subscriptions/${subscriptionId}/funds-sent`, {
    method: 'POST',
    body: attestation,
  });

export const _postCancelSubscription = (subscriptionId: string): Promise<void> =>
  apiInvestmentFetch<void>(`/subscriptions/${subscriptionId}/cancel`, { method: 'POST' });

/**
 * The subscription routes reject illegal transitions with a structured code
 * (`INVESTMENT.SUBSCRIPTION.*`) that tells the wizard which step to send the
 * investor back to — the generic message does not. `ofetch` hands the parsed
 * error body back on `error.data`, same as every other domain reads it.
 */
export const getSubscriptionErrorCode = (error: unknown): string | undefined =>
  (error as TFetchError | undefined)?.data?.code;

/** `InvestmentErrorCodes.SUBSCRIPTION_ILLEGAL_TRANSITION` — the aggregate refused the transition. */
export const SUBSCRIPTION_ILLEGAL_TRANSITION = 'SUBSCRIPTION_ILLEGAL_TRANSITION';

/**
 * The subscription is not on the step the wizard thought it was: the investor
 * advanced it in another tab, or a webhook moved it underneath them.
 *
 * The FE state machine stays the optimistic guard — `canTransition` still
 * decides which buttons are live — so this is only ever reached when the two
 * disagree, and then the server's answer is the true one. 409 is matched
 * alongside the structured code because a conflict status means the same thing
 * whatever code rides with it.
 */
export const isStaleSubscriptionStep = (error: unknown): boolean => {
  if (getSubscriptionErrorCode(error) === SUBSCRIPTION_ILLEGAL_TRANSITION) return true;

  const failure = error as TFetchError | undefined;
  return failure?.status === 409 || failure?.data?.statusCode === 409;
};

/**
 * Re-reads the subscription for exactly those refusals, so the stepper
 * re-syncs to what the backend holds instead of leaving the investor on a step
 * that will keep failing. Reports whether it acted, so callers can keep their
 * own error UI for every other failure.
 */
export const resyncStaleSubscriptionStep = (error: unknown, resync: () => void): boolean => {
  if (!isStaleSubscriptionStep(error)) return false;

  resync();
  return true;
};

/**
 * The above, bound to the wizard's resume read — what every wizard step
 * mutation hands its failures to. Without a fund id there is no resume read to
 * invalidate, so the failure passes straight through.
 */
export const resyncSubscriptionOnConflict = (error: unknown, fundId?: string | null): boolean =>
  !!fundId && resyncStaleSubscriptionStep(error, () => invalidateWizardResume(fundId));

/** `InvestmentErrorCodes.SUBSCRIPTION_ALREADY_ACTIVE` — the fund already holds a live subscription for this investor. */
export const SUBSCRIPTION_ALREADY_ACTIVE = 'SUBSCRIPTION_ALREADY_ACTIVE';

/** `InvestmentErrorCodes.SUBSCRIPTION_MFA_NOT_ENROLLED` — the eligibility submission needs Gamma MFA and the investor has none enrolled. */
export const SUBSCRIPTION_MFA_NOT_ENROLLED = 'SUBSCRIPTION_MFA_NOT_ENROLLED';

/**
 * The start route's own conflict: an investment the wizard did not know about
 * already exists in this fund — opened in another tab, or started before a
 * resume read that has since gone stale.
 *
 * It needs its own check because the route refuses it as a 400 carrying the
 * structured code, not a 409, so `isStaleSubscriptionStep` does not see it. The
 * refetch is the recovery: the resume read then answers with the active
 * subscription and the wizard resolves into it instead of leaving the investor
 * on an amount step that will keep being refused.
 */
export const resyncStartOnConflict = (error: unknown, fundId?: string | null): boolean => {
  if (fundId && getSubscriptionErrorCode(error) === SUBSCRIPTION_ALREADY_ACTIVE) {
    invalidateWizardResume(fundId);
    return true;
  }

  return resyncSubscriptionOnConflict(error, fundId);
};
